// src/pages/admin/Dashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import contractService from '../../services/contractService';
import walletService from '../../services/walletService';
import { useToast } from '../../hooks/useToast';
import { extractApiList } from '../../utils/responseAdapters';
import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faXmark, faFileContract, faUsers, faTriangleExclamation, faWallet,
} from '@fortawesome/free-solid-svg-icons';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysRemaining(endDate) {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / 86400000);
}

// ─── Inline Pending Card ──────────────────────────────────────────────────────

function PendingRequestCard({ contract, onApprove, onReject }) {
  const [acting, setActing] = useState(false);

  const handle = async (action) => {
    setActing(true);
    await action();
    setActing(false);
  };

  return (
    <div className="application-card" style={{ marginBottom: 12 }}>
      <div className="card-header">
        <div>
          <strong>{contract.artist_info?.name || contract.artist_id}</strong>
          <div style={{ fontSize: 13, color: '#888' }}>{contract.artist_info?.email}</div>
        </div>
        <span style={{ fontSize: 12, color: '#888' }}>
          {contract.duration_months}mo · Requested {formatDate(contract.created_at)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
        <Button variant="secondary" disabled={acting} onClick={() => handle(onApprove)}>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6, color: '#10b981' }} />
          Approve
        </Button>
        <Button variant="tertiary" disabled={acting} onClick={() => handle(onReject)}>
          <FontAwesomeIcon icon={faXmark} style={{ marginRight: 6, color: '#ef4444' }} />
          Reject
        </Button>
      </div>
    </div>
  );
}

// ─── Manager Dashboard ────────────────────────────────────────────────────────

export default function ManagerDashboard() {
  const { showToast } = useToast();
  const [pending,  setPending]  = useState([]);
  const [active,   setActive]   = useState([]);
  const [stats,    setStats]    = useState(null);
  const [balance,  setBalance]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contractService.getManagerContracts();
      const all = extractApiList(res?.data);
      setPending(all.filter(c => c.status === 'pending'));
      setActive(all.filter(c => c.status === 'active'));
    } catch {
      showToast('Failed to load contract data', 500);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  useEffect(() => {
    let activeReq = true;
    const loadStats = async () => {
      try {
        const [statsRes, balRes] = await Promise.allSettled([
          contractService.getManagerDashboardStats(),
          walletService.getBalance(),
        ]);
        if (!activeReq) return;
        if (statsRes.status === 'fulfilled') setStats(statsRes.value?.data?.data || null);
        if (balRes.status === 'fulfilled') setBalance(balRes.value?.data?.data || null);
      } catch {
        if (activeReq) setStats(null);
      }
    };
    loadStats();
    return () => { activeReq = false; };
  }, []);

  const handleApprove = async (contractId) => {
    try {
      await contractService.approveContract(contractId);
      showToast('Contract approved', 200);
      await fetchContracts();
    } catch {
      showToast('Failed to approve', 500);
    }
  };

  const handleReject = async (contractId) => {
    try {
      await contractService.rejectContract(contractId);
      showToast('Contract rejected', 200);
      await fetchContracts();
    } catch {
      showToast('Failed to reject', 500);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px', maxWidth: 860 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Managed Artists</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.managed_artists_count ?? active.length}</div>
          </div>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Pending Contracts</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.pending_contracts_count ?? pending.length}</div>
          </div>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Pending Tokenization</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.pending_tokenization_applications ?? 0}</div>
          </div>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Tokenized Assets</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.managed_tokenized_assets ?? 0}</div>
          </div>
          {balance && (
            <Link to="/manager/dashboard/wallet" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="application-card" style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', color: '#fff', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#94a3b8' }}><FontAwesomeIcon icon={faWallet} style={{ marginRight: 4 }} />Wallet</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  ${Number(balance.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* ── Pending Contract Requests ── */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <FontAwesomeIcon icon={faFileContract} style={{ fontSize: 18, color: '#2d8cf0' }} />
            <h3 style={{ margin: 0 }}>Pending Contract Requests</h3>
            {pending.length > 0 && (
              <span style={{
                background: '#ef4444', color: '#fff',
                borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
              }}>
                {pending.length}
              </span>
            )}
            <Link
              to="/manager/dashboard/contracts"
              style={{ marginLeft: 'auto', fontSize: 13, color: '#2d8cf0', textDecoration: 'none' }}
            >
              View all →
            </Link>
          </div>

          {loading ? (
            <p style={{ color: '#aaa' }}>Loading…</p>
          ) : pending.length === 0 ? (
            <div style={{
              padding: '20px', background: '#f9fafb', borderRadius: 10,
              color: '#888', textAlign: 'center', fontSize: 14,
            }}>
              No pending requests. New artist contract requests will appear here.
            </div>
          ) : (
            pending.map(c => (
              <PendingRequestCard
                key={c.contract_id}
                contract={c}
                onApprove={() => handleApprove(c.contract_id)}
                onReject={() => handleReject(c.contract_id)}
              />
            ))
          )}
        </div>

        {/* ── My Managed Artists ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <FontAwesomeIcon icon={faUsers} style={{ fontSize: 18, color: '#10b981' }} />
            <h3 style={{ margin: 0 }}>My Managed Artists</h3>
            <span style={{
              background: '#10b98122', color: '#10b981',
              borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
            }}>
              {active.length}
            </span>
            <Link
              to="/manager/dashboard/contracts"
              style={{ marginLeft: 'auto', fontSize: 13, color: '#2d8cf0', textDecoration: 'none' }}
            >
              Manage →
            </Link>
          </div>

          {loading ? (
            <p style={{ color: '#aaa' }}>Loading…</p>
          ) : active.length === 0 ? (
            <div style={{
              padding: '20px', background: '#f9fafb', borderRadius: 10,
              color: '#888', textAlign: 'center', fontSize: 14,
            }}>
              No managed artists yet. Approve a contract request to start managing an artist.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {active.map(c => {
                const days = daysRemaining(c.end_date);
                const expiring = days !== null && days <= 30;
                return (
                  <div key={c.contract_id} style={{
                    background: expiring ? '#fffbeb' : '#fff',
                    border: `1px solid ${expiring ? '#f59e0b' : '#e5e7eb'}`,
                    borderRadius: 12, padding: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {c.artist_info?.name || c.artist_id}
                    </div>
                    <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
                      {c.artist_info?.email}
                    </div>
                    <div style={{ fontSize: 13, color: '#555' }}>
                      Contract ends: <strong>{formatDate(c.end_date)}</strong>
                    </div>
                    {expiring && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 4 }} />
                        Expires in {days} day{days !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}